import React, { useState, useEffect } from 'react';
import { getPayoutDetails, formatCurrency } from '../../services/payoutService';

const PayoutDetailModal = ({ payoutId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [payout, setPayout] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPayoutDetails();
  }, [payoutId]);

  const fetchPayoutDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await getPayoutDetails(payoutId);
      setPayout(response.data);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to fetch payout details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateRange = (start, end) => {
    const startDate = new Date(start).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    const endDate = new Date(end).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${startDate} - ${endDate}`;
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-800 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30',
      processing: 'text-blue-800 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30',
      completed: 'text-green-800 dark:text-green-300 bg-green-100 dark:bg-green-900/30',
      failed: 'text-red-800 dark:text-red-300 bg-red-100 dark:bg-red-900/30'
    };
    return colors[status] || colors.pending;
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Payout Details
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600 dark:text-gray-300">Loading payout details...</span>
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex">
                <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="ml-3">
                  <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
                </div>
              </div>
            </div>
          ) : payout ? (
            <div className="space-y-6">
              {/* Summary Card */}
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-blue-100 mb-1">Total Amount</p>
                    <p className="text-4xl font-bold">{formatCurrency(payout.amount)}</p>
                    <p className="text-sm text-blue-100 mt-2">
                      {payout.payments?.length || 0} session{(payout.payments?.length || 0) !== 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(payout.status)}`}>
                      {payout.status.charAt(0).toUpperCase() + payout.status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Payout Information */}
              <div className="bg-gray-50 dark:bg-gray-900/30 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Payout Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Payout ID</p>
                    <p className="text-sm font-mono text-gray-900 dark:text-white mt-1">
                      {payout.id}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Period</p>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {formatDateRange(payout.periodStart, payout.periodEnd)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Created</p>
                    <p className="text-sm text-gray-900 dark:text-white mt-1">
                      {formatDate(payout.createdAt)}
                    </p>
                  </div>
                  {payout.processedAt && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Processed</p>
                      <p className="text-sm text-gray-900 dark:text-white mt-1">
                        {formatDate(payout.processedAt)}
                      </p>
                    </div>
                  )}
                  {payout.completedAt && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Completed</p>
                      <p className="text-sm text-gray-900 dark:text-white mt-1">
                        {formatDate(payout.completedAt)}
                      </p>
                    </div>
                  )}
                  {payout.stripePayoutId && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Stripe Payout ID</p>
                      <p className="text-sm font-mono text-gray-900 dark:text-white mt-1">
                        {payout.stripePayoutId}
                      </p>
                    </div>
                  )}
                  {payout.stripeTransferId && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Stripe Transfer ID</p>
                      <p className="text-sm font-mono text-gray-900 dark:text-white mt-1">
                        {payout.stripeTransferId}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Failure Information */}
              {payout.status === 'failed' && payout.failureReason && (
                <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 p-6 rounded">
                  <div className="flex">
                    <svg className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div className="ml-3">
                      <h4 className="text-sm font-semibold text-red-900 dark:text-red-200">
                        Payout Failed
                      </h4>
                      <p className="text-sm text-red-800 dark:text-red-300 mt-1">
                        {payout.failureReason}
                      </p>
                      {payout.failureCode && (
                        <p className="text-xs text-red-700 dark:text-red-400 mt-1">
                          Error code: {payout.failureCode}
                        </p>
                      )}
                      {payout.retryCount > 0 && (
                        <p className="text-xs text-red-700 dark:text-red-400 mt-1">
                          Retry attempts: {payout.retryCount}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Included Payments */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Included Sessions ({payout.payments?.length || 0})
                </h3>

                {payout.payments && payout.payments.length > 0 ? (
                  <div className="space-y-3">
                    {payout.payments.map((payment, index) => (
                      <div
                        key={payment.id}
                        className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-xs font-semibold mr-3">
                                {index + 1}
                              </span>
                              <div>
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  {payment.sessionDate
                                    ? new Date(payment.sessionDate).toLocaleDateString('en-GB', {
                                        weekday: 'short',
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric'
                                      })
                                    : 'Session'}
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-400">
                                  Captured: {new Date(payment.capturedAt).toLocaleDateString('en-GB')}
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="text-right ml-4">
                            <p className="text-sm font-semibold text-gray-900 dark:text-white">
                              {formatCurrency(payment.netAmount)}
                            </p>
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              Gross: {formatCurrency(payment.grossAmount)}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-500">
                              Fee: {formatCurrency(payment.platformFee)}
                            </p>
                          </div>
                        </div>

                        {payment.sessionDuration && (
                          <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-600">
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              Duration: {payment.sessionDuration} minutes
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-600 dark:text-gray-400">
                    No payment details available
                  </div>
                )}
              </div>

              {/* Summary Breakdown */}
              <div className="bg-gray-50 dark:bg-gray-900/30 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Payment Breakdown
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Total Gross Amount:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatCurrency(
                        payout.payments?.reduce((sum, p) => sum + p.grossAmount, 0) || 0
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Platform Commission (10%):</span>
                    <span className="font-medium text-red-600 dark:text-red-400">
                      - {formatCurrency(
                        payout.payments?.reduce((sum, p) => sum + p.platformFee, 0) || 0
                      )}
                    </span>
                  </div>
                  <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between">
                      <span className="text-base font-semibold text-gray-900 dark:text-white">You Receive:</span>
                      <span className="text-lg font-bold text-green-600 dark:text-green-400">
                        {formatCurrency(payout.amount)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PayoutDetailModal;
